import React, { useState } from 'react';
import ServerErrors from '../ServerErrors';

import './index.css';

interface Props {
    promptForNameInput: boolean;
    promptForPhoneInput: boolean;
    promptForEmailInput: boolean;
    isRequestLoading: boolean;
    isRequestSuccessful: boolean;
    serverErrors: string | null;
    submitUserForm: (firstName: string, lastName: string, email: string, phoneNumber: number) => void;
}

const InputForm: React.FC<Props> = ({
    promptForNameInput,
    promptForPhoneInput,
    promptForEmailInput,
    isRequestLoading,
    isRequestSuccessful,
    serverErrors,
    submitUserForm,
}) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');

    const [firstNameError, setFirstNameError] = useState<string | null>(null);
    const [lastNameError, setLastNameError] = useState<string | null>(null);
    const [emailError, setEmailError] = useState<string | null>(null);
    const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null);

    const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Prevent characters from being input into the phone number field - only allow numbers
        if (!/^[0-9]*$/.test(event.target.value)) {
            event.preventDefault();
            return;
        }

        setPhoneNumber(event.target.value);
    };

    const handleOnClick = (event: React.FormEvent<HTMLButtonElement>) => {
        event.preventDefault();

        const firstNameTrimmed = firstName.trim();
        const lastNameTrimmed = lastName.trim();

        const isFirstNameValid = promptForNameInput ? validateNameField(firstNameTrimmed, 'firstName') : true;
        const isLastNameValid = promptForNameInput ? validateNameField(lastNameTrimmed, 'lastName') : true;
        const isEmailValid = promptForEmailInput ? validateEmailField() : true;
        const isPhoneNumberValid = promptForPhoneInput ? validatePhoneNumber() : true;

        if (isFirstNameValid && isLastNameValid && isEmailValid && isPhoneNumberValid) {
            submitUserForm(firstNameTrimmed, lastNameTrimmed, email, parseInt(phoneNumber));
        }
    };

    const validateNameField = (name: string, fieldName: 'firstName' | 'lastName') => {
        if (name.length === 0 || typeof name !== 'string') {
            fieldName === 'firstName'
                ? setFirstNameError('First name is required')
                : setLastNameError('Last name is required');

            return false;
        } else {
            fieldName === 'firstName' ? setFirstNameError(null) : setLastNameError(null);
            return true;
        }
    };

    const validateEmailField = () => {
        const emailPattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        if (!email.length || !emailPattern.test(email)) {
            setEmailError('Email is not valid');
            return false;
        } else {
            setEmailError(null);
            return true;
        }
    };

    const validatePhoneNumber = () => {
        const phoneNumberPattern = /^\(?([0-9]{3})\)?[-.●]?([0-9]{3})[-.●]?([0-9]{4})$/;

        if (!phoneNumber.length) {
            setPhoneNumberError('Phone number is required');
            return false;
        } else if (!phoneNumberPattern.test(phoneNumber)) {
            setPhoneNumberError('Phone number is not valid');
            return false;
        } else {
            setPhoneNumberError(null);
            return true;
        }
    };

    return (
        <form className="input-form-wrapper">
            {promptForNameInput && (
                <>
                    <input
                        id="first-name-input"
                        className="input-form-field"
                        type="text"
                        placeholder="First name"
                        value={firstName}
                        onChange={(event) => setFirstName(event.target.value)}
                        onBlur={() => validateNameField(firstName, 'firstName')}
                    />
                    {firstNameError && (
                        <div id="first-name-validation" className="input-form-inline-validation-error">
                            <span className="input-form-inline-validation-error-text">{firstNameError}</span>
                        </div>
                    )}
                    <input
                        id="last-name-input"
                        className="input-form-field"
                        type="text"
                        placeholder="Last name"
                        value={lastName}
                        onChange={(event) => setLastName(event.target.value)}
                        onBlur={() => validateNameField(lastName, 'lastName')}
                    />
                    {lastNameError && (
                        <div id="last-name-validation" className="input-form-inline-validation-error">
                            <span className="input-form-inline-validation-error-text">{lastNameError}</span>
                        </div>
                    )}
                </>
            )}

            {promptForEmailInput && (
                <>
                    <input
                        id="email-input"
                        className="input-form-field"
                        type="email"
                        placeholder="Email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        onBlur={() => validateEmailField()}
                    />
                    {emailError && (
                        <div id="email-validation" className="input-form-inline-validation-error">
                            <span className="input-form-inline-validation-error-text">{emailError}</span>
                        </div>
                    )}
                </>
            )}

            {promptForPhoneInput && (
                <>
                    <input
                        id="phone-number-input"
                        className="input-form-field"
                        pattern="[0-9]{10}"
                        type="tel"
                        placeholder="Phone number"
                        value={phoneNumber}
                        onChange={handlePhoneNumberChange}
                    />
                    {phoneNumberError && (
                        <div id="phone-number-validation" className="input-form-inline-validation-error">
                            <span className="input-form-inline-validation-error-text">{phoneNumberError}</span>
                        </div>
                    )}
                </>
            )}

            {serverErrors && <ServerErrors error={serverErrors} />}

            <button
                id="sign-up-button"
                disabled={isRequestLoading || isRequestSuccessful}
                className="input-form-button"
                type="submit"
                onClick={handleOnClick}
            >
                Sign Up
            </button>
        </form>
    );
};

export default InputForm;
